import { Button, Icon, Input, Modal, Typography } from '@smartgift/frontend-toolkit';
import { useFormik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { FORM_ERROR_MESSAGES, MAX_EMAIL_LENGTH, dateFormat } from '@hero/constants';
import { useGetUser } from '@hero/services/organization-service/hooks';
import { useNotification } from '@hero/store/hooks';
import { formatDate } from '@hero/utils';

import * as S from './style';

const validationSchema = yup.object().shape({
  contacts: yup.array().of(
    yup.object().shape({
      email: yup
        .string()
        .max(MAX_EMAIL_LENGTH, FORM_ERROR_MESSAGES.max50Char)
        .email(FORM_ERROR_MESSAGES.invalidEmail)
        .required(FORM_ERROR_MESSAGES.emailRequired)
    })
  )
});

function DownloadReport({ isOpen, onRequestClose, onAsyncSubmit, dateRange = {} }) {
  const { t } = useTranslation();
  const { data: userData } = useGetUser();
  const { showSuccess } = useNotification();
  const [isLoading, setIsLoading] = useState(false);

  const removeFromList = index => {
    const contacts = [...formik.values.contacts];
    contacts.splice(index, 1);
    formik.setValues({ ...formik.values, contacts });
  };

  const updateForm = () => {
    const contacts = [...formik.values.contacts];
    contacts.push({ email: '' });
    formik.setValues({ ...formik.values, contacts });
  };

  const formik = useFormik({
    initialValues: {
      contacts: [{ email: userData?.email }]
    },
    validationSchema,
    onSubmit: async data => {
      setIsLoading(true);
      await onAsyncSubmit(data).then(() => {
        formik.resetForm();
        onRequestClose();
        showSuccess({
          message: t('yourReportSent'),
          type: 'toast'
        });
      });
      setIsLoading(false);
    }
  });

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      title={t('downloadReport')}
      size="sm"
      primaryButtonProps={{
        children: t('send'),
        onClick: () => formik.submitForm(),
        disabled: !formik.isValid || isLoading || !formik.values?.contacts?.length
      }}
      secondaryButtonProps={{
        children: t('cancel')
      }}
    >
      <S.DownloadReport>
        {Object.keys(dateRange)?.length > 0 && (
          <Typography variant="body1Bold">
            {formatDate(dateRange.startDate, {
              format: dateFormat,
              includeTime: false
            })}{' '}
            -{' '}
            {formatDate(dateRange.endDate, {
              format: dateFormat,
              includeTime: false
            })}
          </Typography>
        )}

        <Typography variant="body2">{t('downloadReportText')}</Typography>

        <S.DownloadReportInputs>
          {formik?.values?.contacts?.map((contact, index) => (
            <S.InputGroup
              key={`contact-$index}`}
              isError={formik?.errors?.contacts?.[index]?.email}
            >
              <Input
                name={`contact-${index}`}
                variant="outlined"
                label={`${t('email')}*`}
                onChange={e => {
                  const contacts = [...formik.values.contacts];
                  contacts[index] = { email: e.target.value };
                  formik.setValues({ ...formik.values, contacts });
                }}
                value={contact.email}
                inputProps={{
                  maxLength: MAX_EMAIL_LENGTH
                }}
                error={formik?.errors?.contacts?.[index]?.email}
                helperText={formik?.errors?.contacts?.[index]?.email}
                block
              />
              <Button
                startIcon={<Icon name="delete" />}
                variant="text"
                onClick={() => removeFromList(index)}
                color="error"
              >
                {t('remove')}
              </Button>
            </S.InputGroup>
          ))}
        </S.DownloadReportInputs>

        <Button
          color="primary"
          variant="text"
          onClick={updateForm}
          disabled={!formik.isValid}
          startIcon={<Icon name="add" />}
        >
          {t('addAnotherEmail')}
        </Button>
      </S.DownloadReport>
    </Modal>
  );
}

export { DownloadReport };
